import type { StateCreator } from 'zustand';
import type { CurrentUserQuery, FeatureFeatureNameString } from '../../graphql';
import { Currency } from '../../graphql';
import type { JWTToken } from '../../lib/cookieParsers';
import type { UserOrgRight } from '../../lib/auth';
import { getCurrentUserRightsByOrgId } from '../../lib/auth';
import { globalStore, useGlobalStore } from './store';
import compact from 'lodash/compact';

export const userFromQueryData = (
  data: CurrentUserQuery | undefined,
  jwt: JWTToken | null,
): User | undefined =>
  data?.currentUser
    ? {
        ...data.currentUser,
        currentUserIsSystemAdmin: data.currentUserIsSystemAdmin,
        jwt,
        myMail: data.myMail ?? undefined,
      }
    : undefined;

export type User = CurrentUserQuery['currentUser'] & {
  currentUserIsSystemAdmin: boolean | null | undefined;
  jwt: JWTToken | null;
  myMail: string | null | undefined;
};

export type CurrentUserSlice = {
  currentUser: User | null;
  defaultCurrency: Currency;
  features: string[];
};

export const createCurrentUserSlice: StateCreator<CurrentUserSlice> = () => ({
  currentUser: null,
  defaultCurrency: Currency.EUR,
  features: [],
});

/*
 * Memoized selectors (not a required pattern anymore)
 * https://github.com/pmndrs/zustand/discussions/387#discussioncomment-3810458
 */
const currentUserSelector = (s: CurrentUserSlice) => s.currentUser;
const defaultCurrencySelector = (s: CurrentUserSlice) => s.defaultCurrency;

export function useCurrentUser(): User | null {
  return useGlobalStore<User | null>(currentUserSelector);
}

export function useDefaultCurrency() {
  return useGlobalStore(defaultCurrencySelector);
}

type PlanningMode = 'voyage_charter' | 'order';
export function usePlanningMode(): PlanningMode {
  return useGlobalStore((store) => store.features.includes('shipowner_coa'))
    ? 'voyage_charter'
    : 'order';
}

/*
 * Utility functions
 */

/** Reads from store directly so do not use if reactivity is required */
export function hasUserRight(orgId: number, right: UserOrgRight): boolean {
  const { currentUser } = globalStore.getState();
  if (!currentUser) {
    console.error('hasUserRight called with no current user');
    return false;
  }
  return (
    (currentUser &&
      getCurrentUserRightsByOrgId(currentUser.userRights).get(orgId)?.[
        right
      ]) ??
    false
  );
}

export function getCurrentUserFeatures(currentUser: User) {
  return compact(
    currentUser?.organization?.organizationFeaturesByOrganizationId?.nodes?.map(
      (orgFeature) => orgFeature?.featureName,
    ),
  );
}

export function userHasFeature(user: User | null, featureName: string) {
  return (
    !!user &&
    getCurrentUserFeatures(user).includes(
      featureName as FeatureFeatureNameString,
    )
  );
}

/** Reads from store directly so do not use if reactivity is required */
export function hasFeature(featureName: string): boolean {
  const { features } = globalStore.getState();
  return features.includes(featureName);
}

/** Reads from store directly so do not use if reactivity is required */
export function hasOrganizationConfig(
  configName: keyof Pick<
    NonNullable<User['organization']>,
    'configCargoAllocationCombineAssortment' | 'configEnableDefaultLaycan'
  >,
): boolean {
  const { currentUser } = globalStore.getState();
  if (!currentUser) {
    console.error('hasOrganizationConfig called with no current user');
    return false;
  }
  return currentUser?.organization?.[configName] ?? false;
}
